import React from 'react'
import { DirectionEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { MIN_COLUMN_WIDTH } from '@ztechapps/webui.data-tools'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { PivotFilterPopover } from '../FieldPanel/PivotFilterPopover'
import { findPivotLayoutItem } from '../pivot/pivotLayout.utils'
import { isSamePivotValueColumn, toPivotLayoutValueColumn } from '../pivot/pivotValueColumn.utils'
import { usePivotGridContext } from './Context'
import { usePivotViewerContext } from '../PivotViewer/Context'
import styles from './PivotGridHead.module.scss'
import rowStyles from './PivotGridRow.module.scss'
import { PivotResizeHandle } from './PivotResizeHandle'
import { renderPivotMemberLabel } from './renderPivotMemberLabel'

/** Renders the pivot grid column headers. */
export const PivotGridHead = () => {
  const {
    result,
    headerRows,
    leafColumns,
    renderCell,
    data,
    filters,
    timeZone,
    onFilter,
    measuredWidths,
    fixedWidths,
    memberSorts,
    onMemberSort,
    valueSort,
    onValueSort,
  } = usePivotGridContext()
  // Fullscreen portal target for the sort tooltip — undefined off-fullscreen
  // so the overlay keeps its default document.body portal.
  const { container } = usePivotViewerContext()

  return (
    <thead>
      {headerRows.map((cells, rowIndex) => (
        <tr key={`header:${rowIndex}`} aria-rowindex={rowIndex + 1}>
          <ShowIf condition={rowIndex === 0}>
            {result.rowFields.map((field, level) => {
              const fixed = fixedWidths[level]
              const stack = (
                <HorizontalStack gap={1}>
                  <span className={`${rowStyles.flexTruncate} text-truncate flex-grow-1`}>{field.field}</span>
                  <PivotFilterPopover
                    data={data}
                    field={field}
                    filter={findPivotLayoutItem(filters, field)}
                    timeZone={timeZone}
                    onSave={(excludedValues, dateRange) => onFilter(field, excludedValues, dateRange)}
                    sortDirection={findPivotLayoutItem(memberSorts, field)?.direction}
                    onSort={(direction) => onMemberSort(field, direction)}
                  />
                  <PivotResizeHandle index={level} width={fixed ?? measuredWidths[level] ?? MIN_COLUMN_WIDTH} />
                </HorizontalStack>
              )
              return (
                <th
                  key={`${field.field}:${level}`}
                  rowSpan={headerRows.length}
                  className="text-nowrap bg-body-secondary"
                  style={{ width: fixed ?? measuredWidths[level] }}
                >
                  {/* Same wrapper element in both states — only its containment toggles — so a
                  drag mid-gesture (which flips `fixed` from undefined to a number) never swaps the
                  element tree and remounts `PivotResizeHandle`, freezing the drag. Unsized renders
                  `d-block w-100` so the flex row fills the `th` and the handle lands at the
                  cell's right edge; sized keeps `d-block w-100` and adds `sizedHeader`, whose
                  inline-size containment stops the content from setting the column's minimum
                  while the box still fills the rendered column. */}
                  <div className={fixed === undefined ? 'd-block w-100' : `d-block w-100 ${styles.sizedHeader}`}>
                    {stack}
                  </div>
                </th>
              )
            })}
          </ShowIf>
          {cells.map((cell, index) => {
            const fixed = rowIndex === headerRows.length - 1 ? fixedWidths[result.rowFields.length + index] : undefined
            const valueColumn = cell.isMeasure ? toPivotLayoutValueColumn(result, leafColumns[index]) : undefined
            const direction =
              valueColumn && valueSort && isSamePivotValueColumn(valueColumn, valueSort)
                ? valueSort.direction
                : undefined
            const tooltip =
              direction === DirectionEnum.desc
                ? 'Click to sort ascending'
                : direction === DirectionEnum.asc
                  ? 'Click to clear sort'
                  : 'Click to sort descending'
            const label =
              direction === DirectionEnum.desc
                ? `Sort ${cell.label} ascending`
                : direction === DirectionEnum.asc
                  ? `Clear sort on ${cell.label}`
                  : `Sort ${cell.label} descending`
            const content = valueColumn ? (
              <HorizontalStack gap={1} className="justify-content-end">
                <span className={`${rowStyles.flexTruncate} text-truncate flex-grow-1 text-end`}>{cell.label}</span>
                <span className={direction ? undefined : styles.sortButton}>
                  <BusyButton
                    variant="link"
                    bsSize="sm"
                    className="p-0"
                    icon={direction === DirectionEnum.asc ? 'arrow-up' : 'arrow-down'}
                    active={!!direction}
                    aria-label={label}
                    title={tooltip}
                    container={container}
                    onClick={() => onValueSort(valueColumn)}
                  />
                </span>
                <PivotResizeHandle
                  index={result.rowFields.length + index}
                  width={fixed ?? measuredWidths[result.rowFields.length + index] ?? MIN_COLUMN_WIDTH}
                />
              </HorizontalStack>
            ) : (
              renderPivotMemberLabel({
                renderCell,
                field: cell.field,
                label: cell.memberLabel ?? cell.label,
                isTotal: !!cell.isTotal,
                isBlank: !!cell.isBlank,
              })
            )
            return (
              <th
                key={cell.key}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
                className={
                  cell.isMeasure
                    ? `text-nowrap text-end bg-body-secondary ${styles.measureHeader}`
                    : 'text-nowrap bg-body-secondary'
                }
                aria-sort={
                  direction === DirectionEnum.desc
                    ? 'descending'
                    : direction === DirectionEnum.asc
                      ? 'ascending'
                      : undefined
                }
                style={
                  rowIndex === headerRows.length - 1
                    ? { width: fixed ?? measuredWidths[result.rowFields.length + index] }
                    : undefined
                }
              >
                {/* Same wrapper element in both states — see the Rows-field header above. */}
                <div
                  className={fixed === undefined ? 'd-inline-block align-top' : `d-block w-100 ${styles.sizedHeader}`}
                >
                  {content}
                </div>
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}
